// A Drive folder read live, as collapsible rows at any depth. One view of "what
// is actually on file", shared by a client's Files tab and a staff member's.
// What a page adds to a file row comes in as fileNotes (the line under its name)
// and fileBadge (beside it).
import { useState } from 'react';
import { format } from 'date-fns';
import OpenLink from './OpenLink';
import { filesUnder, fileKind, fmtSize } from '../lib/driveListing';

export const fmtDay = (iso) => (iso ? format(new Date(iso), 'MMM d, yyyy') : '');

const st = {
  kind: { fontSize: 10.5, fontWeight: 600, padding: '2px 0', borderRadius: 4, background: 'var(--card-bg)', color: 'var(--text2)', border: '1px solid var(--border)', textAlign: 'center', width: 44, flexShrink: 0 },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' },
};

// ── one folder, collapsed by default ─────────────────────────────────────────
const byNewest = (a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime));
const count = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const Chevron = ({ open }) => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}><path d="M6 3l5 5-5 5" /></svg>;
// What a folder holds, said once for a top-level row and a nested one alike.
const summary = (folder) => {
  const files = filesUnder(folder);
  const subs = (folder.subfolders || []).length + (folder.folderCount || 0);
  return { files, subs, latest: [...files].sort(byNewest)[0]?.modifiedTime, empty: !files.length && !subs };
};
const summaryText = ({ files, subs, empty }) => (empty ? 'empty' : [files.length ? count(files.length, 'file') : null, subs ? count(subs, 'folder') : null].filter(Boolean).join(' · '));

export function FolderRow({ folder, open, onToggle, fileNotes, fileBadge, onUpload, note }) {
  const sum = summary(folder);
  const notes = { fileNotes, fileBadge, rootName: folder.name };
  return (
    <div id={`folder-${folder.id}`}>
      <div role="button" tabIndex={0} aria-expanded={open}
        onClick={onToggle} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto', gap: 10, alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }}
        className="doc-folder-row">
        <Chevron open={open} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: sum.empty ? 500 : 600, color: sum.empty ? 'var(--text3)' : 'var(--text)' }}>{folder.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{summaryText(sum)}</span>
          {sum.latest && <span style={{ fontSize: 12, color: 'var(--text3)' }}>latest {fmtDay(sum.latest)}</span>}
        </div>
        <span onClick={(e) => e.stopPropagation()}><OpenLink href={folder.webViewLink} className="btn-ghost">Open folder</OpenLink></span>
      </div>
      {open && (
        <div style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
          {[...(folder.subfolders || [])].sort((a, b) => a.name.localeCompare(b.name)).map((sf) => (
            <SubFolder key={sf.id} folder={sf} depth={1} path={`${folder.name}/${sf.name}`} notes={notes} onUpload={onUpload} />
          ))}
          {[...(folder.files || [])].sort(byNewest).map((f) => <FileRow key={f.id} file={f} {...notes} />)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '8px 16px 8px 44px', fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
            <span>{sum.empty && !note ? 'Nothing here yet.' : note}</span>
            <button type="button" style={st.linkBtn} onClick={() => onUpload(folder.name)}>Upload to {folder.name}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// A folder inside a folder, collapsed by default and indented by its depth.
// path is where an upload from here lands ("PA Submissions/Transfer in · Sep 2026").
function SubFolder({ folder, depth, path, notes, onUpload }) {
  const [open, setOpen] = useState(false);
  const sum = summary(folder);
  const indent = 44 + (depth - 1) * 22;
  return (
    <div>
      <div role="button" tabIndex={0} aria-expanded={open}
        onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ display: 'flex', gap: 10, alignItems: 'center', padding: `8px 16px 8px ${indent - 26}px`, borderTop: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }}
        className="doc-folder-row">
        <Chevron open={open} />
        <span style={st.kind}>Folder</span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: sum.empty ? 'var(--text3)' : 'var(--text)' }}>{folder.name}</span>
          <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{summaryText(sum)}{sum.latest ? ` · latest ${fmtDay(sum.latest)}` : ''}</span>
        </div>
        <span onClick={(e) => e.stopPropagation()}><OpenLink href={folder.webViewLink} className="btn-ghost" style={{ flexShrink: 0 }}>Open</OpenLink></span>
      </div>
      {open && (
        <>
          {[...(folder.subfolders || [])].sort((a, b) => a.name.localeCompare(b.name)).map((sf) => (
            <SubFolder key={sf.id} folder={sf} depth={depth + 1} path={`${path}/${sf.name}`} notes={notes} onUpload={onUpload} />
          ))}
          {[...(folder.files || [])].sort(byNewest).map((f) => <FileRow key={f.id} file={f} {...notes} indent={indent + 22} />)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: `6px 16px 6px ${indent + 22}px`, fontSize: 12, color: 'var(--text3)', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <span>{folder.folderCount ? `${count(folder.folderCount, 'more folder')} inside — open it in Drive to see them.` : sum.empty ? 'Nothing in this folder yet.' : ''}</span>
            <button type="button" style={st.linkBtn} onClick={() => onUpload(path)}>Upload to {folder.name}</button>
          </div>
        </>
      )}
    </div>
  );
}

export function FileRow({ file: f, fileNotes, fileBadge, rootName, indent = 44 }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: `7px 16px 7px ${indent}px`, borderTop: '1px solid var(--border)' }}>
      <span style={st.kind}>{fileKind(f.mimeType, f.name)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', gap: 8, alignItems: 'center' }}>
          {fileBadge?.(f, rootName)}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {f.path && <span style={{ color: 'var(--accent)' }}>{f.path} ·</span>}
          {f.modifiedTime && <span>{fmtDay(f.modifiedTime)}</span>}
          {f.size && <span>· {fmtSize(f.size)}</span>}
          {fileNotes?.(f)}
        </div>
      </div>
      <OpenLink href={f.webViewLink} className="btn-ghost" style={{ flexShrink: 0 }}>Open</OpenLink>
    </div>
  );
}
